import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import Hls from 'hls.js';
import Layout from '../components/layout/Layout';
import { Video, Send, ChevronUp, Megaphone, Radio, Users } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

function voterId() {
  let id = localStorage.getItem('dca_webinar_voter');
  if (!id) { id = 'v_' + Math.random().toString(36).slice(2, 12); localStorage.setItem('dca_webinar_voter', id); }
  return id;
}

function parseYouTubeId(url) {
  if (!url) return '';
  if (!/[/.]/.test(url)) return url; // already an id
  const m = url.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|live\/|v\/))([\w-]{6,})/);
  return m ? m[1] : '';
}
function parseVimeoId(url) {
  if (!url) return '';
  const m = url.match(/vimeo\.com\/(?:video\/)?(\d+)/);
  return m ? m[1] : (/^\d+$/.test(url) ? url : '');
}

function detectVideoType(url) {
  if (!url) return 'zoom';
  if (/\.m3u8($|\?)/i.test(url)) return 'hls';
  if (/\.mp4($|\?)/i.test(url)) return 'mp4';
  if (/vimeo\.com/i.test(url)) return 'vimeo';
  if (/youtu/i.test(url)) return 'youtube';
  return 'mp4';
}

// Native HLS player (.m3u8) using hls.js with Safari native fallback
function HlsPlayer({ src }) {
  const ref = useRef(null);
  useEffect(() => {
    const video = ref.current;
    if (!video || !src) return;
    let hls;
    if (video.canPlayType('application/vnd.apple.mpegurl')) {
      video.src = src;
    } else if (Hls.isSupported()) {
      hls = new Hls({ lowLatencyMode: true });
      hls.loadSource(src);
      hls.attachMedia(video);
    }
    return () => { if (hls) hls.destroy(); };
  }, [src]);
  return <video ref={ref} controls autoPlay playsInline className="w-full h-full bg-black" data-testid="hls-player" />;
}

function NativeVideo({ room }) {
  const type = room.video_type || 'zoom';
  if (type === 'youtube') {
    const id = parseYouTubeId(room.stream_url);
    if (!id) return <PlayerFallback room={room} />;
    return (
      <iframe
        title={room.title}
        src={`https://www.youtube.com/embed/${id}?autoplay=1&rel=0`}
        className="w-full h-full"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
        data-testid="youtube-player"
      />
    );
  }
  if (type === 'vimeo') {
    const id = parseVimeoId(room.stream_url);
    if (!id) return <PlayerFallback room={room} />;
    return (
      <iframe
        title={room.title}
        src={`https://player.vimeo.com/video/${id}?autoplay=1`}
        className="w-full h-full"
        allow="autoplay; fullscreen; picture-in-picture"
        allowFullScreen
        data-testid="vimeo-player"
      />
    );
  }
  if (type === 'hls' && room.stream_url) return <HlsPlayer src={room.stream_url} />;
  if (type === 'mp4' && room.stream_url) {
    return <video controls autoPlay playsInline className="w-full h-full bg-black" src={room.stream_url} data-testid="mp4-player" />;
  }
  return <PlayerFallback room={room} />;
}

function PlayerFallback({ room }) {
  return (
    <div className="w-full h-full flex flex-col items-center justify-center text-center p-8">
      <Video size={48} className="text-white/40 mb-3" />
      {room.join_url ? (
        <>
          <p className="text-white/80 mb-4">{room.status === 'live' ? 'The session is live now.' : 'Join the live session when it starts.'}</p>
          <a href={room.join_url} target="_blank" rel="noreferrer" className="btn-primary-orange text-sm inline-block" data-testid="join-zoom">Join Live Session</a>
        </>
      ) : <p className="text-white/60">The stream will appear here when the host goes live.</p>}
    </div>
  );
}

function RegistrationGate({ slug, room, onRegistered }) {
  const [form, setForm] = useState({ name: '', email: '', phone: '' });
  const [submitting, setSubmitting] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) return toast.error('Name and email required');
    setSubmitting(true);
    try {
      const r = await axios.post(`${API}/api/webinars/${slug}/register`, form);
      localStorage.setItem(`dca_webinar_token_${slug}`, r.data.join_token);
      toast.success('You are registered!');
      onRegistered(r.data.join_token);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Registration failed');
    } finally { setSubmitting(false); }
  };
  return (
    <div className="max-w-md mx-auto bg-white border border-[#E2E8F0] rounded-2xl p-6 mt-10" data-testid="webinar-registration">
      <div className="text-xs uppercase font-bold text-[#7C3AED] mb-1">Register to attend</div>
      <h2 className="text-xl font-bold text-[#0A0F1C] mb-1">{room.title}</h2>
      {room.scheduled_at && <p className="text-sm text-[#475569] mb-4">{new Date(room.scheduled_at).toLocaleString()}</p>}
      <form onSubmit={submit} className="space-y-2">
        <input className="w-full px-3 py-2 border rounded text-sm" placeholder="Full name" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} data-testid="reg-name" />
        <input className="w-full px-3 py-2 border rounded text-sm" placeholder="Email" type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} data-testid="reg-email" />
        <input className="w-full px-3 py-2 border rounded text-sm" placeholder="Phone (optional)" value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} data-testid="reg-phone" />
        <button disabled={submitting} className="btn-primary-orange text-sm w-full disabled:opacity-60" data-testid="reg-submit">{submitting ? 'Registering…' : 'Register & Enter Room'}</button>
      </form>
    </div>
  );
}

function Countdown({ target }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => { const t = setInterval(() => setNow(Date.now()), 1000); return () => clearInterval(t); }, []);
  const ms = new Date(target).getTime() - now;
  if (isNaN(ms) || ms <= 0) return null;
  const d = Math.floor(ms / 86400000), h = Math.floor(ms / 3600000) % 24, m = Math.floor(ms / 60000) % 60, s = Math.floor(ms / 1000) % 60;
  const cell = (v, l) => (
    <div className="flex flex-col items-center" data-testid={`cd-${l}`}>
      <span className="text-2xl sm:text-3xl font-bold text-white tabular-nums">{String(v).padStart(2, '0')}</span>
      <span className="text-[10px] uppercase tracking-wider text-white/60">{l}</span>
    </div>
  );
  return (
    <div className="bg-[#0A0F1C] rounded-2xl p-5 flex items-center justify-center gap-4 sm:gap-6" data-testid="webinar-countdown">
      <span className="text-xs uppercase font-bold text-[#F97316] mr-1">Starts in</span>
      {d > 0 && <>{cell(d, 'days')}<span className="text-white/30 text-2xl">:</span></>}
      {cell(h, 'hrs')}<span className="text-white/30 text-2xl">:</span>
      {cell(m, 'min')}<span className="text-white/30 text-2xl">:</span>
      {cell(s, 'sec')}
    </div>
  );
}

export default function WebinarRoomPage() {
  const { slug } = useParams();
  const [searchParams] = useSearchParams();
  const [room, setRoom] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [poll, setPoll] = useState(null);
  const [cta, setCta] = useState(null);
  const [liveCount, setLiveCount] = useState(0);
  const [text, setText] = useState('');
  const [name, setName] = useState('');
  const [voted, setVoted] = useState({});
  const [joinToken, setJoinToken] = useState(
    searchParams.get('t') || localStorage.getItem(`dca_webinar_token_${slug}`) || ''
  );
  const vid = useRef(voterId());

  const poll_ = useCallback(async () => {
    try {
      const [q, p, c, s] = await Promise.all([
        axios.get(`${API}/api/webinars/${slug}/questions`),
        axios.get(`${API}/api/webinars/${slug}/polls/active`),
        axios.get(`${API}/api/webinars/${slug}/cta`),
        axios.get(`${API}/api/webinars/${slug}/live-stats`),
      ]);
      setQuestions(q.data || []);
      setPoll(p.data.poll);
      setCta(c.data.cta);
      setLiveCount(s.data.live || 0);
    } catch { /* ignore transient */ }
  }, [slug]);

  useEffect(() => {
    axios.get(`${API}/api/webinars/${slug}`).then(r => setRoom(r.data)).catch(() => setRoom(false));
  }, [slug]);

  // Persist a join token passed via URL
  useEffect(() => {
    const t = searchParams.get('t');
    if (t) localStorage.setItem(`dca_webinar_token_${slug}`, t);
  }, [slug, searchParams]);

  const needsRegistration = room && room.registration_required && !joinToken;

  // Poll engagement + send presence heartbeat once admitted
  useEffect(() => {
    if (!room || needsRegistration) return;
    poll_();
    const t = setInterval(poll_, 4000);
    const beat = () => axios.post(`${API}/api/webinars/${slug}/heartbeat`, { viewer_id: vid.current, join_token: joinToken }).catch(() => {});
    beat();
    const hb = setInterval(beat, 15000);
    return () => { clearInterval(t); clearInterval(hb); };
  }, [room, needsRegistration, poll_, slug, joinToken]);

  if (room === null) return <Layout><div className="max-w-3xl mx-auto p-8 text-center text-[#94A3B8]">Loading…</div></Layout>;
  if (room === false) return <Layout><div className="max-w-3xl mx-auto p-8 text-center"><h1 className="text-2xl font-bold">Webinar not found</h1></div></Layout>;

  if (needsRegistration) {
    return <Layout title={room.title}><div className="max-w-6xl mx-auto px-4 lg:px-8 py-8"><RegistrationGate slug={slug} room={room} onRegistered={setJoinToken} /></div></Layout>;
  }

  const submitQuestion = async (e) => {
    e.preventDefault();
    if (text.trim().length < 3) return toast.error('Type a question first');
    try { await axios.post(`${API}/api/webinars/${slug}/questions`, { text, attendee_name: name || 'Anonymous' }); setText(''); toast.success('Question submitted — pending host approval'); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  const upvote = async (qid) => {
    try { await axios.post(`${API}/api/webinars/${slug}/questions/${qid}/upvote`, { voter_id: vid.current }); poll_(); } catch { /* noop */ }
  };
  const vote = async (optId) => {
    try { await axios.post(`${API}/api/webinars/${slug}/polls/${poll.poll_id}/vote`, { option_id: optId, voter_id: vid.current }); setVoted({ ...voted, [poll.poll_id]: true }); poll_(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  const clickCta = async () => { try { await axios.post(`${API}/api/webinars/${slug}/cta/${cta.cta_id}/click`); } catch { /* noop */ } window.open(cta.url, '_blank'); };

  const totalVotes = poll ? (poll.total_votes || 0) : 0;
  const hasVoted = poll && voted[poll.poll_id];

  // After the webinar ends, swap the player to the recording if one is set.
  const isReplay = room.status === 'ended' && !!room.replay_url;
  const playerRoom = isReplay
    ? { ...room, video_type: detectVideoType(room.replay_url), stream_url: room.replay_url }
    : room;

  return (
    <Layout title={room.title}>
      <div className="max-w-6xl mx-auto px-4 lg:px-8 py-8">
        <div className="flex items-center gap-3 mb-2 flex-wrap">
          <h1 className="text-2xl sm:text-3xl font-bold text-[#0A0F1C]" data-testid="room-title">{room.title}</h1>
          {room.status === 'live' && <span className="inline-flex items-center gap-1 text-xs font-bold text-white bg-red-600 px-2.5 py-1 rounded-full animate-pulse"><Radio size={12} />LIVE</span>}
          {room.status === 'ended' && !isReplay && <span className="text-xs font-bold text-slate-600 bg-slate-100 px-2.5 py-1 rounded-full">ENDED</span>}
          {isReplay && <span className="inline-flex items-center gap-1 text-xs font-bold text-white bg-[#7C3AED] px-2.5 py-1 rounded-full" data-testid="replay-badge"><Video size={12} />RECORDING</span>}
          <span className="inline-flex items-center gap-1 text-xs font-bold text-[#1D4ED8] bg-[#EFF6FF] px-2.5 py-1 rounded-full" data-testid="live-count"><Users size={12} />{liveCount} watching</span>
        </div>
        {room.description && <p className="text-[#475569] mb-6">{room.description}</p>}

        {cta && (
          <div className="mb-6 bg-gradient-to-r from-[#7C3AED] to-[#F97316] rounded-xl p-4 flex items-center justify-between flex-wrap gap-3" data-testid="cta-banner">
            <div className="text-white"><div className="font-bold flex items-center gap-2"><Megaphone size={16} />{cta.label}</div>{cta.description && <div className="text-sm text-white/85">{cta.description}</div>}</div>
            <button onClick={clickCta} className="bg-white text-[#0F172A] font-bold text-sm px-4 py-2 rounded-lg" data-testid="cta-click">{cta.label} →</button>
          </div>
        )}

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Video */}
          <div className="lg:col-span-2 space-y-4">
            {room.status === 'scheduled' && room.scheduled_at && <Countdown target={room.scheduled_at} />}
            <div className="bg-[#0A0F1C] rounded-2xl aspect-video overflow-hidden flex items-center justify-center" data-testid="video-panel">
              <NativeVideo room={playerRoom} />
            </div>

            {/* Active poll */}
            {poll && (
              <div className="bg-white border border-[#E2E8F0] rounded-2xl p-5" data-testid="active-poll">
                <div className="text-xs uppercase font-bold text-[#7C3AED] mb-1">Live Poll</div>
                <div className="font-bold text-[#0A0F1C] mb-3">{poll.question}</div>
                <div className="space-y-2">
                  {poll.options.map(o => {
                    const pct = o.votes != null && totalVotes ? Math.round(o.votes / totalVotes * 100) : 0;
                    return (
                      <button key={o.id} onClick={() => !hasVoted && vote(o.id)} disabled={hasVoted} className={`w-full text-left border rounded-lg p-3 relative overflow-hidden ${hasVoted ? 'cursor-default' : 'hover:border-[#7C3AED]'} border-[#E2E8F0]`} data-testid={`poll-opt-${o.id}`}>
                        {o.votes != null && <div className="absolute inset-0 bg-[#F5F3FF]" style={{ width: `${pct}%` }} />}
                        <div className="relative flex justify-between text-sm"><span>{o.text}</span>{o.votes != null && <span className="font-bold">{pct}%</span>}</div>
                      </button>
                    );
                  })}
                </div>
                {hasVoted && <div className="text-xs text-green-700 mt-2">Thanks for voting!</div>}
              </div>
            )}
          </div>

          {/* Q&A */}
          <div className="space-y-3">
            <div className="bg-white border border-[#E2E8F0] rounded-2xl p-4">
              <h2 className="font-bold text-[#0A0F1C] mb-3">Ask a question</h2>
              <form onSubmit={submitQuestion} className="space-y-2" data-testid="qa-form">
                <input className="w-full px-3 py-2 border rounded text-sm" placeholder="Your name (optional)" value={name} onChange={e => setName(e.target.value)} data-testid="qa-name" />
                <div className="flex gap-2">
                  <input className="flex-1 px-3 py-2 border rounded text-sm" placeholder="Type your question…" value={text} onChange={e => setText(e.target.value)} data-testid="qa-input" />
                  <button className="btn-primary-orange text-sm px-3" data-testid="qa-submit"><Send size={14} /></button>
                </div>
              </form>
            </div>
            <div className="bg-white border border-[#E2E8F0] rounded-2xl divide-y max-h-[480px] overflow-y-auto" data-testid="qa-list">
              {questions.map(q => (
                <div key={q.question_id} className="p-3 flex gap-2" data-testid={`pubq-${q.question_id}`}>
                  <button onClick={() => upvote(q.question_id)} className="flex flex-col items-center text-[#7C3AED] shrink-0" data-testid={`upvote-${q.question_id}`}><ChevronUp size={16} /><span className="text-xs font-bold">{q.upvotes || 0}</span></button>
                  <div className="min-w-0">
                    <div className="text-sm">{q.is_pinned && <span className="text-[#F97316] text-xs font-bold mr-1">📌</span>}{q.text}</div>
                    <div className="text-xs text-[#94A3B8]">{q.attendee_name}</div>
                    {q.status === 'answered' && q.answer && <div className="text-xs text-green-700 mt-1 bg-green-50 rounded p-2">Answer: {q.answer}</div>}
                    {q.status === 'answered' && !q.answer && <span className="text-[10px] uppercase text-green-700">Answered live</span>}
                  </div>
                </div>
              ))}
              {questions.length === 0 && <div className="p-6 text-center text-sm text-[#94A3B8]">No questions yet — be the first!</div>}
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}
